import { i18n } from '@/libraries/i18n'

export type ToastType = 'info' | 'warning' | 'error'

interface ToastOptions {
  body: string
  header?: string
  type?: ToastType
  autohide?: boolean
}

const TYPE_CLASSES: Record<ToastType, string> = {
  info: '',
  warning: 'toast-warning',
  error: 'toast-error',
}

function defaultHeader(type: ToastType): string | undefined {
  switch (type) {
    case 'error':
      return i18n.t('common.feedback.error_occurred')
    case 'warning':
      return i18n.t('common.labels.note')
    default:
      return undefined
  }
}

function escapeHtml(text: string): string {
  const div = document.createElement('div')
  div.textContent = text
  return div.innerHTML
}

// Mirrors the markup of Luca::Component::Bootstrap::Toast so JS-raised and server-rendered flashes
// look identical. Appending to #flash-list is enough to make it visible: flash_list_controller
// observes that element and shows anything with a .toast class.
export function showToast({ body, header, type = 'info', autohide = type === 'info' }: ToastOptions): HTMLElement {
  const toastHeader = header ?? defaultHeader(type)
  const closeButton = `<button type="button" class="btn-close" data-bs-dismiss="toast" aria-label="${i18n.t('common.actions.close')}"></button>`

  const inner = toastHeader
    ? `<div class="toast-header">
         <strong class="me-auto">${escapeHtml(toastHeader)}</strong>
         ${closeButton}
       </div>
       <div class="toast-body">${escapeHtml(body)}</div>`
    : `<div class="toast-body">
         <div class="d-flex justify-content-between align-items-start">
           <div class="flex-grow-1">${escapeHtml(body)}</div>
           ${closeButton}
         </div>
       </div>`

  const wrapper = document.createElement('div')
  wrapper.innerHTML = `<div class="toast ${TYPE_CLASSES[type]}" role="alert" aria-live="assertive" aria-atomic="true"
       data-bs-autohide="${autohide}">${inner}</div>`.trim()

  const toast = wrapper.firstElementChild as HTMLElement
  document.getElementById('flash-list')?.appendChild(toast)

  return toast
}
